/**
 * Session controller — the brain of a practice run.
 *
 * Each turn it assembles a small candidate pool (due review clues + a tag-biased
 * random sample) and hands it to the pure selection policy. On each self-grade
 * it advances the FSRS card, nudges every tag the clue carries, updates stats,
 * and writes through to IndexedDB.
 *
 * Adaptive ("all categories") mode targets weak tags: once enough evidence
 * exists it samples mostly from the weakest tags plus a couple of
 * under-explored ones for coverage; before then it samples broadly so skill
 * estimates can form.
 */
import { review as fsrsReview } from './fsrs';
import { rankWeakTags, selectNextClue } from './selection';
import { freshTagSkill, gradeOutcome, updateTagSkill } from './tagSkill';
import { Grade, type CardState, type Clue, type PracticeMode, type SessionStats, type TagSkill } from './types';
import type { ClueRepo } from '../data/repo';
import type { ProgressStore } from '../data/progress';

const POOL_SIZE = 60;
const WEAK_TAG_COUNT = 4;
const EXPLORE_TAG_COUNT = 2;
const MIN_ATTEMPTS_FOR_WEAKNESS = 3;

function todayKey(now: number): string {
  return new Date(now).toISOString().slice(0, 10);
}

export class Session {
  readonly mode: PracticeMode;
  private repo: ClueRepo;
  private progress: ProgressStore;
  private allTagIds: number[];
  private rng: () => number;
  readonly sessionSeen = new Set<number>();
  stats: SessionStats = { presented: 0, known: 0, shaky: 0, missed: 0 };

  constructor(
    repo: ClueRepo,
    progress: ProgressStore,
    mode: PracticeMode,
    allTagIds: number[],
    rng: () => number = Math.random,
  ) {
    this.repo = repo;
    this.progress = progress;
    this.mode = mode;
    this.allTagIds = allTagIds;
    this.rng = rng;
  }

  /** Tags to sample from this turn given the mode and current skills. */
  private targetTags(): number[] {
    if (this.mode.kind === 'tag') return [this.mode.tagId];

    const ranked = rankWeakTags(this.progress.skills, MIN_ATTEMPTS_FOR_WEAKNESS);
    if (ranked.length < 3) return []; // cold start: sample across everything

    const weak = ranked.slice(0, WEAK_TAG_COUNT).map((t) => t.tagId);
    const weakSet = new Set(weak);
    const explore: number[] = [];
    const pool = this.allTagIds.filter((t) => !weakSet.has(t));
    for (let i = 0; i < EXPLORE_TAG_COUNT && pool.length > 0; i++) {
      const idx = Math.floor(this.rng() * pool.length);
      explore.push(pool.splice(idx, 1)[0]!);
    }
    return [...weak, ...explore];
  }

  /** Build the candidate pool: due review clues (in-mode) + a fresh sample. */
  private buildPool(now: number): Clue[] {
    const seen = [...this.sessionSeen];
    let sample: Clue[];
    if (this.mode.kind === 'category') {
      sample = this.repo.sampleByCategory(this.mode.title, POOL_SIZE, seen);
    } else if (this.mode.kind === 'deck') {
      sample = this.repo.sampleByDeck(this.mode.deckId, POOL_SIZE, seen);
    } else {
      sample = this.repo.sampleByTags(this.targetTags(), POOL_SIZE, seen);
    }

    // Include due review cards, restricted to the current mode's scope. (Deck
    // membership isn't carried on the clue, so deck mode relies on the sample to
    // surface due cards — still FSRS-prioritized when present.)
    let due: Clue[] = [];
    if (this.mode.kind !== 'deck') {
      const dueIds = this.progress.dueCardIds(now).filter((id) => !this.sessionSeen.has(id));
      due = this.repo.getClues(dueIds.slice(0, POOL_SIZE));
      if (this.mode.kind === 'tag') {
        const tag = this.mode.tagId;
        due = due.filter((c) => c.t.includes(tag));
      } else if (this.mode.kind === 'category') {
        const title = this.mode.title;
        due = due.filter((c) => c.c === title);
      }
    }

    // Merge, de-duplicating by id.
    const byId = new Map<number, Clue>();
    for (const c of [...due, ...sample]) byId.set(c.i, c);
    return [...byId.values()];
  }

  /** Pick the next clue, or null if the pool is somehow empty. */
  nextClue(now: number = Date.now()): Clue | null {
    const pool = this.buildPool(now);
    return selectNextClue({
      pool,
      cards: this.progress.cards,
      skills: this.progress.skills,
      now,
      sessionSeen: this.sessionSeen,
      rng: this.rng,
    });
  }

  /** Apply a self-grade to the just-revealed clue. Persists asynchronously. */
  async grade(clue: Clue, grade: Grade, now: number = Date.now()): Promise<void> {
    this.sessionSeen.add(clue.i);

    const prevCard: CardState | null = this.progress.cards.get(clue.i) ?? null;
    const card = fsrsReview(prevCard, clue.i, grade, now);

    const updatedSkills: TagSkill[] = [];
    for (const tagId of clue.t) {
      const prev = this.progress.skills.get(tagId) ?? freshTagSkill(tagId);
      updatedSkills.push(updateTagSkill(prev, clue.df, grade));
    }

    // Session + lifetime stats.
    this.stats.presented++;
    this.progress.stats.presented++;
    if (grade === Grade.Known) {
      this.stats.known++;
      this.progress.stats.known++;
    } else if (grade === Grade.Shaky) {
      this.stats.shaky++;
      this.progress.stats.shaky++;
    } else {
      this.stats.missed++;
      this.progress.stats.missed++;
    }
    const day = todayKey(now);
    this.progress.stats.daily[day] = (this.progress.stats.daily[day] ?? 0) + 1;

    await this.progress.persist(card, updatedSkills);
  }

  /** Convenience for the reveal UI: outcome score of a grade (for any animation). */
  static outcomeScore(grade: Grade): number {
    return gradeOutcome(grade);
  }
}
